import { AfterViewInit, Component, inject, OnInit, DOCUMENT } from '@angular/core';
import { IconRegistryService } from '@core/icon-registry/icon-registry.service';
import { SessionTimeoutService } from '@core/services/session-timeout.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { CommonsFacade } from '@state/commons';
import moment from 'moment-timezone';
import { NavigationEnd, Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { environment } from '@environment';
import { debounceTime, filter } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { OpenReplayService } from '@core/services/open-replay.service';
import { MobileDialogBackButtonService } from '@shared/mobile-dialog-back-button.service';
import { isIosSafariBrowser } from '@core/services/browser-detection';

const iosSafariRootScrollDisabledClass = 'ios-safari-root-scroll-disabled';
const apiPingRoutes = new Set(['login', 'new-account', 'user-settings']);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly document = inject(DOCUMENT);
  private readonly iconRegistry = inject(IconRegistryService);
  private readonly sessionTimeout = inject(SessionTimeoutService);
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly snackService = inject(StackedMessagesService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly openReplayService = inject(OpenReplayService);
  private readonly mobileDialogBackButton = inject(MobileDialogBackButtonService);
  private readonly titleService = inject(Title);

  private APIErrorShown = false;
  private lastAPIPingUrl: string | null = null;

  private GOOGLE_ANALYTICS_ENABLED = environment.GOOGLE_ANALYTICS_ENABLED;
  private PAGE_TITLE = environment.PAGE_TITLE;

  constructor() {
    this.iconRegistry.registerIcons();
    this.mobileDialogBackButton.init();
  }

  public ngOnInit(): void {
    moment.tz.setDefault('Europe/Warsaw');

    this.disableRootScrollOnIosSafari();

    this.titleService.setTitle(this.PAGE_TITLE);
    this.openReplayService.init();

    this.deviceInfoService.startObservingDevice();
    this.analyticsService.initGoogleAnalytics();

    if (this.GOOGLE_ANALYTICS_ENABLED) {
      this.handleRouteEvents();
    }

    this.handleApiPingRouteEvents();
    this.pingApiForUrl(this.router.url);
  }

  public ngAfterViewInit(): void {
    this.document.documentElement.style.setProperty('--font-factor', '0px');

    this.commonsFacade.lastAPIPingSuccessfull$.subscribe(connected => {
      if (connected) {
        if (this.APIErrorShown) {
          this.snackService.close();
          this.snackService.showInfo({ message: 'COMMON.NOTIFICATION.SERVER_CONNECTION_RESTORED' });
          this.APIErrorShown = false;
        }
        return;
      }

      if (!this.APIErrorShown) {
        this.snackService.close();
        this.snackService.showError({ message: 'COMMON.ERROR.SERVER_CONNECTION_LOST' });
        this.APIErrorShown = true;
      }
    });

    const loaderEl = this.document.getElementById('main-app-loader-container');
    loaderEl.parentElement.removeChild(loaderEl);

    this.sessionTimeout.setupIdleWatch();
  }

  handleRouteEvents() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        debounceTime(500)
      )
      .subscribe((event: NavigationEnd) => {
        this.analyticsService.trackEvent('user_event', 'user_changed_url', {
          URL: event.urlAfterRedirects,
        });
      });
  }

  private handleApiPingRouteEvents(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.pingApiForUrl(event.urlAfterRedirects));
  }

  private pingApiForUrl(url: string): void {
    const routePath = this.router.parseUrl(url).root.children.primary?.segments[0]?.path;

    if (!apiPingRoutes.has(routePath) || this.lastAPIPingUrl === url) {
      return;
    }

    this.lastAPIPingUrl = url;
    this.commonsFacade.pingApi();
  }

  private disableRootScrollOnIosSafari(): void {
    const { maxTouchPoints, platform, userAgent, vendor } = window.navigator;

    if (!isIosSafariBrowser({ maxTouchPoints, platform, userAgent, vendor })) {
      return;
    }

    this.document.documentElement.classList.add(iosSafariRootScrollDisabledClass);
    this.document.body.classList.add(iosSafariRootScrollDisabledClass);
  }
}
